/**
 * The entry form's state and save logic (docs/08 §8.3, docs/03 §3.1).
 *
 * Saving always goes through the local queue first, then the network. That is
 * what makes the online and offline paths identical from the volunteer's
 * point of view, and what guarantees a save is never lost because the
 * connection dropped between tapping Save and the server answering.
 */

import { useCallback, useMemo, useState } from "react";
import { ApiRequestError, isolates as isolatesApi } from "@/lib/api";
import { db, markState, queueEdit, saveLocalIsolate } from "@/lib/db";
import { blocking, buildContext, byField, evaluate, type Finding } from "@/lib/qa";
import { syncWorker } from "@/lib/sync";
import type {
  IsolatePayload,
  Isolate,
  Organism,
  QARuleConfig,
  Specimen,
} from "@/lib/types";
import { uuid } from "@/lib/utils";

/** Fields carried onto the next organism from the same specimen (E-11). */
export const SHARED_SPECIMEN_FIELDS = [
  "lab_number",
  "hospital_number",
  "age_value",
  "age_unit",
  "sex",
  "ward_raw",
  "ward_code",
  "specimen_date",
  "specimen_type",
  "culture_result",
  "register_book",
  "register_page",
] as const satisfies readonly (keyof IsolatePayload)[];

export function emptyIsolate(): IsolatePayload {
  return {
    lab_number: null,
    hospital_number: null,
    age_value: null,
    age_unit: "years",
    sex: null,
    ward_raw: null,
    ward_code: null,
    specimen_date: null,
    specimen_type: null,
    culture_result: null,
    organism_code: null,
    organism_as_written: null,
    isolate_number: "1st",
    ast_results: {},
    status: "complete",
    register_book: null,
    register_page: null,
    notes: null,
  };
}

export type SaveMode = "new" | "next-organism" | "draft";

export interface SaveOutcome {
  ok: boolean;
  recordId: string | null;
  /** The server's id, once it has one. Null while the row is only queued. */
  isolateId: string | null;
  /** True when it went into the queue rather than straight to the server. */
  queued: boolean;
  findings: Finding[];
}

export function useEntryForm({
  organisms,
  specimens,
  qaRules,
  initial,
  editId,
  expectedUpdatedAt,
}: {
  organisms: Organism[];
  specimens: Specimen[];
  qaRules: QARuleConfig[];
  initial?: Partial<IsolatePayload>;
  editId?: string | null;
  expectedUpdatedAt?: string | null;
}) {
  const [values, setValues] = useState<IsolatePayload>({
    ...emptyIsolate(),
    ...initial,
  });
  const [saving, setSaving] = useState(false);
  /** Set once the volunteer has tried to save, so warnings are not premature. */
  const [attempted, setAttempted] = useState(false);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const setAllValues = useCallback((newValues: Partial<IsolatePayload>) => {
    setValues((current) => ({
      ...current,
      ...newValues,
    }));
  }, []);

  const context = useMemo(
    () => buildContext(organisms, specimens),
    [organisms, specimens],
  );

  // Live validation, re-run on every keystroke (docs/08 §8.3).
  const findings = useMemo(
    () => evaluate(values, context, qaRules),
    [values, context, qaRules],
  );

  const visibleFindings = useMemo(
    () =>
      findings.filter(
        (f) => f.severity === "blocking" || (attempted && !dismissed.has(f.code)),
      ),
    [findings, attempted, dismissed],
  );

  const findingsByField = useMemo(() => byField(visibleFindings), [visibleFindings]);
  const blockingFindings = useMemo(() => blocking(findings), [findings]);

  const setField = useCallback(
    <K extends keyof IsolatePayload>(field: K, value: IsolatePayload[K]) => {
      setValues((current) => {
        const next = { ...current, [field]: value };

        // E-09: choosing no growth clears and hides the organism section, so
        // stale values cannot be submitted behind a hidden card.
        if (
          field === "culture_result" &&
          (value === "no_growth" || value === "no_significant_growth")
        ) {
          next.organism_code = null;
          next.organism_as_written = null;
          next.ast_results = {};
        }
        return next;
      });
    },
    [],
  );

  const setAst = useCallback((code: string, value: IsolatePayload["ast_results"][string]) => {
    setValues((current) => ({
      ...current,
      ast_results: { ...current.ast_results, [code]: value },
    }));
  }, []);

  /** Warnings can be dismissed once (docs/03 §3.2). */
  const dismissFinding = useCallback((code: string) => {
    setDismissed((current) => new Set(current).add(code));
  }, []);

  const reset = useCallback(
    (mode: SaveMode) => {
      setAttempted(false);
      setDismissed(new Set());
      setValues((current) => {
        if (mode !== "next-organism") return emptyIsolate();

        // E-11: keep the specimen, clear the organism and its results.
        const carried = emptyIsolate();
        for (const field of SHARED_SPECIMEN_FIELDS) {
          // A per-field assignment keeps each type intact; indexing with a
          // string would mean casting the whole payload away.
          Object.assign(carried, { [field]: current[field] });
        }
        carried.isolate_number = current.isolate_number === "1st" ? "2nd" : "3rd";
        return carried;
      });
    },
    [],
  );

  const save = useCallback(
    async (mode: SaveMode): Promise<SaveOutcome> => {
      setAttempted(true);

      const isDraft = mode === "draft";
      const payload: IsolatePayload = {
        ...values,
        status: isDraft ? "draft" : "complete",
      };

      // A draft may be parked with blocking rules outstanding; a complete row
      // may not (docs/03 §3.2).
      if (!isDraft && blockingFindings.length > 0) {
        return {
          ok: false,
          recordId: null,
          isolateId: null,
          queued: false,
          findings: blockingFindings,
        };
      }

      setSaving(true);

      if (editId) {
        try {
          const localRecord = await db.isolates_local.get(editId);
          if (localRecord && !localRecord.server_id) {
            await db.isolates_local.update(editId, {
              payload,
              updated_at: new Date().toISOString(),
            });
            if (mode === "next-organism") reset(mode);
            return {
              ok: true,
              recordId: localRecord.record_id,
              isolateId: null,
              queued: true,
              findings,
            };
          }

          const targetServerId = localRecord?.server_id ?? editId;
          try {
            const updated = await isolatesApi.update(targetServerId, {
              ...payload,
              status: isDraft ? "draft" : "complete",
              expected_updated_at: expectedUpdatedAt ?? null,
              allow_blocking: isDraft,
            });

            if (localRecord) {
              await markState(localRecord.client_uuid, "synced", {
                payload,
                record_id: updated.record_id,
              });
            } else {
              const byServerId = await db.isolates_local
                .filter((r) => r.server_id === targetServerId)
                .first();
              if (byServerId) {
                await markState(byServerId.client_uuid, "synced", {
                  payload,
                  record_id: updated.record_id,
                });
              }
            }

            if (mode === "next-organism") reset(mode);
            return {
              ok: true,
              recordId: updated.record_id,
              isolateId: updated.id,
              queued: false,
              findings,
            };
          } catch (error) {
            if (error instanceof ApiRequestError && error.isOffline) {
              const clientUuid = uuid();
              await queueEdit(targetServerId, clientUuid, payload, expectedUpdatedAt ?? null);
              if (mode === "next-organism") reset(mode);
              return {
                ok: true,
                recordId: null,
                isolateId: targetServerId,
                queued: true,
                findings,
              };
            }
            if (error instanceof ApiRequestError && error.status === 422) {
              const serverFindings =
                (error.detail?.findings as Finding[] | undefined) ?? [];
              return {
                ok: false,
                recordId: null,
                isolateId: targetServerId,
                queued: false,
                findings: serverFindings,
              };
            }
            throw error;
          }
        } finally {
          setSaving(false);
        }
      }

      const clientUuid = uuid();

      try {
        // Always write locally first: if the tab dies mid-request the record
        // is still here (docs/09 §9.4, the online path).
        await saveLocalIsolate(payload, { clientUuid });

        try {
          const saved: Isolate = await isolatesApi.create({
            ...payload,
            client_uuid: clientUuid,
            client_created_at: new Date().toISOString(),
            allow_blocking: isDraft,
          });
          await markState(clientUuid, "synced", {
            record_id: saved.record_id,
            server_id: saved.id,
          });
          reset(mode);
          return {
            ok: true,
            recordId: saved.record_id,
            isolateId: saved.id,
            queued: false,
            findings,
          };
        } catch (error) {
          if (error instanceof ApiRequestError && error.isOffline) {
            // Offline: it stays queued and the worker will take it (O-04).
            void syncWorker.refreshCounts();
            reset(mode);
            return {
              ok: true,
              recordId: null,
              isolateId: null,
              queued: true,
              findings,
            };
          }

          if (error instanceof ApiRequestError && error.status === 422) {
            // The server's rules disagreed with ours: the server wins.
            await markState(clientUuid, "conflict", {
              error: error.message,
              errorDetail: error.detail,
            });
            const serverFindings =
              (error.detail?.findings as Finding[] | undefined) ?? [];
            return {
              ok: false,
              recordId: null,
              isolateId: null,
              queued: false,
              findings: serverFindings,
            };
          }

          await markState(clientUuid, "error", { error: String(error) });
          throw error;
        }
      } finally {
        setSaving(false);
      }
    },
    [values, blockingFindings, findings, reset, editId, expectedUpdatedAt],
  );

  return {
    values,
    setValues,
    setAllValues,
    setField,
    setAst,
    findings,
    visibleFindings,
    findingsByField,
    blockingFindings,
    dismissFinding,
    save,
    saving,
    reset,
    /** E-09: hide the organism card entirely on a no-growth result. */
    showOrganismSection:
      values.culture_result !== "no_growth" &&
      values.culture_result !== "no_significant_growth",
  };
}
